import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';

import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import universityAPI, { UNIVERSITY_ENDPOINT } from 'src/api/university';
import { SWRConfig } from 'swr';

import Seo from '@components/atoms/seo';
import Header from '@components/organisms/academicLandingPage/header';
import AcademicLandingPage from '@components/templates/academicLandingPage';
import Main from '@components/templates/main';
import { useInsider } from '@hooks/dataTracking/useInsider';
import { IUniversityLandingPageData } from '@interfaces/university';

type UniversityLandingPageProps = {
  fallback: Record<string, { data: unknown }>;
};

const Academic = ({ fallback }: UniversityLandingPageProps) => {
  const router = useRouter();

  const universityIdName = router.query.universityIdName as string;
  const landingPageData = fallback[UNIVERSITY_ENDPOINT.UNIVERSITY_INFORMATION_LANDING_PAGE(universityIdName)].data as {
    university: IUniversityLandingPageData;
  };

  useInsider({
    autoLoad: true,
  });

  return (
    <SWRConfig value={{ fallback }}>
      <Main>
        <Seo
          title={landingPageData?.university?.nameEn || ''}
          description={landingPageData?.university?.annotation || ''}
        />
        <Header />
        <AcademicLandingPage />
      </Main>
    </SWRConfig>
  );
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const locale = ctx.locale as string;
  const universityIdName = (ctx.params?.universityIdName as string) || '';

  const universityInfo = await universityAPI.getUniversityInfomation(universityIdName);

  if (!universityInfo?.data?.data) {
    return {
      redirect: {
        destination: '/not-found',
        permanent: false,
      },
    };
  }

  if (!universityInfo?.data?.data?.university?.isEnabled) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  return {
    props: {
      fallback: {
        [UNIVERSITY_ENDPOINT.UNIVERSITY_INFORMATION_LANDING_PAGE(universityIdName)]: universityInfo.data || [],
      },
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default Academic;
